import React from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { BookOpen } from 'lucide-react';
import { ArticleItem } from './types';

interface ArticleCardProps {
  article: ArticleItem;
  showCategory?: boolean;
}

export default function ArticleCard({ article, showCategory = true, }: ArticleCardProps) {

  return (
    <Link
      href={`/resources/${article?.slug}`}
      className="group flex flex-col justify-between bg-white dark:bg-[#00020F66] rounded-xl border border-gray-200/80 dark:border-white/10 overflow-hidden hover:shadow-lg transition-all duration-300 h-full"
    >
      <div>
        {/* Image / Placeholder */}
        <div className="relative aspect-14/8 w-full overflow-hidden bg-gray-100 dark:bg-neutral-800">
          {article?.badge && (
            <span className="absolute top-3 left-3 z-10 px-2.5 py-0.5 rounded-full text-[9px] font-bold tracking-wider uppercase bg-[#2563EB] text-white shadow-xs">
              {article.badge}
            </span>
          )}
          {article?.image ? (
            <>
              {/* Ambient blurred backdrop so any aspect ratio fits seamlessly without harsh letterbox bars */}
              <Image
                src={article.image}
                alt=""
                fill
                aria-hidden="true"
                className="object-cover rounded-md  blur-md scale-110 opacity-30 dark:opacity-20 pointer-events-none select-none"
              />
              <Image
                src={article.image}
                alt={article?.title || ''}
                fill
                sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
                className="object-contain  rounded-md group-hover:scale-110 scale-105 transition-transform duration-500 relative z-1"
              />
            </>
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-blue-50/70 to-blue-100/40 dark:from-neutral-800/80 dark:to-neutral-900 text-gray-400 dark:text-neutral-500">
              <BookOpen className="size-7 stroke-[1.5] text-blue-400/80 dark:text-blue-500/60" />
              <span className="text-[10px] font-semibold tracking-wider uppercase mt-1.5 text-gray-400 dark:text-neutral-500">
                ArthaVerse
              </span>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-3.5 sm:p-5 pb-0">
          <div
            className={`text-[11px] sm:text-xs font-semibold ${showCategory && article?.category ? 'text-[#2563EB]' : 'text-gray-400'
              }`}
          >
            {showCategory && article?.category
              ? `${article.category} • ${article?.readTime || ''}`
              : article?.readTime}
          </div>
          <h4 className="text-sm sm:text-base font-bold font-secondary text-gray-900 dark:text-white mt-1.5 leading-snug group-hover:text-[#2563EB] transition-colors line-clamp-2">
            {article?.title}
          </h4>
        </div>
      </div>

      {/* Author Footer */}
      <div className="p-3.5 sm:p-5 pt-3 sm:pt-4 mt-2.5 sm:mt-3 border-t border-gray-100 dark:border-white/10 flex items-center gap-2.5">
        {article?.author?.avatar ? (
          <div className="relative size-8 shrink-0 aspect-square rounded-full overflow-hidden border border-gray-200 dark:border-gray-700">
            <Image
              src={article.author.avatar}
              alt={article?.author?.name || ''}
              fill
              className="object-cover"
            />
          </div>
        ) : (
          <div className="size-8 shrink-0 aspect-square rounded-full bg-blue-50 dark:bg-blue-950/50 border border-blue-200/50 dark:border-blue-900/40 text-[#2563EB] flex items-center justify-center font-bold text-xs uppercase">
            {article?.author?.name ? article.author.name[0] : 'A'}
          </div>
        )}
        <div className="min-w-0">
          <div className="text-xs font-bold text-gray-900 dark:text-white truncate">
            {article?.author?.name}
          </div>
          {article?.publishedDate && (
            <div className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
              {article.publishedDate}
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}
